// Save-at-finish commit for the native printer setup wizard: NOTHING
// persists before this runs, so cancelling the wizard at any step needs zero
// cleanup (unlike the desktop wizard's cleanupAddedDevice — do not port it).
// Pure store-injected function → unit-testable against the real Pinia store.
import {
  IPersistedPrinterSettings,
  usePrintingStore,
} from '@/composition/use-printing';
import { IPrinter } from '@/interfaces/IPrinter';
import { INativeDestinationSelection } from '@/composition/printing/use-native-printing-ui';

/** Device chosen in the wizard's Device step (wizard-local until Finish). */
export interface INativeWizardDevice {
  type: IPrinter['type'];
  /** string | number: USB ids are numeric and must reach the plugin as-is. */
  id: IPrinter['id'];
  name: string;
  host?: string;
  port?: number;
  /** True = network printer the wizard must save on Finish (not saved yet). */
  isNew: boolean;
  /** Device-step probe results (network only). */
  reachable?: boolean;
  supportsDleEot?: boolean;
}

export interface INativeWizardCommitInput {
  device: INativeWizardDevice;
  /** Final printer name (only editable for isNew network printers). */
  name: string;
  /** Type step's explicit choice — persisted as printerSettings[name].type. */
  printerType: 'escpos' | 'zpl';
  selection: INativeDestinationSelection[];
  /**
   * Codepage picked in the Test step's symbols flow; null = untouched.
   * Written to the PRINTER's settings (per-printer map) — the store's
   * deterministic recompute then refreshes the legacy destination
   * encodings for rollback compatibility.
   */
  encoding: string | null;
}

/**
 * Commits the finished wizard in order: (1) save the new network printer
 * (collision throws BEFORE any assignment is written — a failed finish
 * leaves zero partial state), (2) apply checked assignments, (3) persist the
 * Type step's choice (+ ZPL defaults / picked codepage) into the per-printer
 * settings map — setPrinterSettings runs the deterministic legacy recompute,
 * which refreshes destination encodings + the stickerSettings mirror. The
 * store-wide watcher persists; returns the concrete printer for post-commit
 * UI (probe chip seeding).
 */
export function commitNativeWizard(
  store: ReturnType<typeof usePrintingStore>,
  input: INativeWizardCommitInput
): IPrinter {
  const { device, selection } = input;
  const name = input.name.trim();

  let printer: IPrinter;
  if (device.isNew) {
    if (!device.host || device.port === undefined) {
      throw new Error('Falta la dirección de la impresora');
    }
    store.addNetworkPrinter({
      name,
      host: device.host,
      port: device.port,
      supportsDleEot: device.supportsDleEot ?? false,
    });
    printer = { type: 'tcp', id: `${device.host}:${device.port}`, name };
  } else {
    // USB/BT names are the device-reported assignment keys — never renamed.
    printer = { type: device.type, id: device.id, name: device.name };
  }

  for (const entry of selection) {
    if (entry.destination === 'ticket') {
      store.setTicketPrinter(printer);
    } else if (entry.destination === 'sticker') {
      store.setStickerPrinter(printer);
    } else if (entry.stationId !== undefined) {
      store.setComandaPrinter(entry.stationId, printer);
    }
  }

  // Merge over any existing entry (a usb/bt printer may have been configured
  // through the edit modal before a wizard re-run) — the wizard only owns
  // the fields it collected.
  const existing = store.printerSettings[printer.name];
  const settings: IPersistedPrinterSettings = {
    ...existing,
    type: input.printerType,
  };
  if (input.printerType === 'zpl' && !settings.zpl) {
    // Materialize from the RESOLVED chain (explicit → legacy localStorage
    // stickerSettings → defaults), never from DEFAULT_ZPL_SETTINGS: a wizard
    // re-run over a migrated sticker printer must adopt the user's tuned
    // legacy geometry, not stomp it (the recompute mirror would then
    // overwrite the legacy localStorage copy too — unrecoverable even by
    // rollback). printerSettingsOf returns freshly-normalized clones.
    settings.zpl = store.printerSettingsOf(printer).zpl;
  }
  if (input.encoding) {
    settings.escpos = { ...settings.escpos, codepage: input.encoding };
  }
  store.setPrinterSettings(printer.name, settings);

  return printer;
}
